const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://viewrapi-876455037250.asia-northeast3.run.app";

/** 백엔드 `device_pairing.SESSION_TTL_SEC` 와 반드시 동일 */
export const DEVICE_PAIRING_TTL_SEC = 120;
export const DEVICE_PAIRING_TTL_MS = DEVICE_PAIRING_TTL_SEC * 1000;

export function formatPairingCountdown(remainingMs: number): string {
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export interface CreatePairingSessionResponse {
  session_id: string;
  short_code: string;
  expires_at: number;
  pair_url: string;
  server_time?: number;
  status?: string;
  /** Monotonic local deadline, adjusted using server time and request latency. */
  deadline?: number;
}

export interface PairingStatusResponse {
  status: string;
  custom_token: string | null;
  session_id: string;
}

export interface PairingAttachment {
  sessionId: string;
  consumerKey: string;
}

const TRANSIENT_RETRY_DELAYS_MS = [250, 750];

function isTransientHttpStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function createPairingConsumerKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${crypto.randomUUID()}${crypto.randomUUID()}`;
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  }
  throw new Error("Secure random generation is unavailable");
}

async function fetchWithTransientRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= TRANSIENT_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const res = await fetch(input, init);
      if (
        !isTransientHttpStatus(res.status) ||
        attempt === TRANSIENT_RETRY_DELAYS_MS.length
      ) {
        return res;
      }
      lastError = new Error(`Temporary pairing API error (${res.status})`);
    } catch (error) {
      lastError = error;
      if (attempt === TRANSIENT_RETRY_DELAYS_MS.length) throw error;
    }

    await new Promise<void>((resolve) => {
      setTimeout(resolve, TRANSIENT_RETRY_DELAYS_MS[attempt]);
    });
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Temporary pairing API error");
}

function withPairingDeadline(data: CreatePairingSessionResponse, started: number): CreatePairingSessionResponse {
  const received = performance.now();
  const remaining = typeof data.server_time === "number"
    ? (data.expires_at - data.server_time) * 1000 - (received - started) / 2
    : data.expires_at * 1000 - Date.now();
  return { ...data, deadline: received + Math.max(0, remaining) };
}

export class DevicePairingSessionError extends Error {
  constructor(readonly status: number) {
    super("Could not update the Vision Pro code. Please try again.");
  }
}

export async function createPairingSession(
  idToken?: string,
  rotateSessionId?: string,
): Promise<CreatePairingSessionResponse> {
  const started = performance.now();
  const res = await (idToken ? fetchWithTransientRetry : fetch)(`${API_BASE_URL}/api/device-pairing/sessions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: idToken && rotateSessionId ? JSON.stringify({ rotate_session_id: rotateSessionId }) : undefined,
    cache: "no-store",
  });
  if (!res.ok) throw new DevicePairingSessionError(res.status);
  return withPairingDeadline(await res.json(), started);
}

/** Polling does not create or renew a code. Works across independent browsers. */
export async function fetchAccountPairingSession(idToken: string): Promise<CreatePairingSessionResponse | null> {
  const started = performance.now();
  const res = await fetchWithTransientRetry(`${API_BASE_URL}/api/device-pairing/sessions/current`, {
    headers: { Authorization: `Bearer ${idToken}` }, cache: "no-store",
  });
  if (!res.ok) throw new DevicePairingSessionError(res.status);
  const data = await res.json();
  return data ? withPairingDeadline(data, started) : null;
}

/** Cancel: discards the account code on every path (CODE-06·15). 404 means already gone. */
export async function cancelPairingSession(sessionId: string, idToken: string): Promise<void> {
  const res = await fetchWithTransientRetry(
    `${API_BASE_URL}/api/device-pairing/sessions/${encodeURIComponent(sessionId)}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${idToken}` } },
  );
  if (!res.ok && res.status !== 404) throw new DevicePairingSessionError(res.status);
}

export async function completePairingSession(
  sessionId: string,
  idToken: string,
  clientEmail?: string | null,
  displayName?: string | null,
): Promise<Response> {
  const body: Record<string, string> = {};
  const email = (clientEmail ?? "").trim();
  if (email) body.email = email;
  const name = (displayName ?? "").trim();
  if (name) body.display_name = name;

  return fetchWithTransientRetry(
    `${API_BASE_URL}/api/device-pairing/sessions/${encodeURIComponent(sessionId)}/complete`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    },
  );
}

export async function fetchPairingStatus(
  sessionId: string,
  consumerKey?: string,
): Promise<PairingStatusResponse> {
  const res = await fetchWithTransientRetry(
    `${API_BASE_URL}/api/device-pairing/sessions/${encodeURIComponent(sessionId)}/status`,
    consumerKey
      ? { headers: { "X-Device-Pairing-Key": consumerKey } }
      : undefined,
  );
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as PairingStatusResponse;
}

/** Firebase custom-token 로그인 성공 후에만 서버의 일시 세션을 정리한다. */
export async function consumePairingSession(
  sessionId: string,
  idToken: string,
): Promise<void> {
  const res = await fetchWithTransientRetry(
    `${API_BASE_URL}/api/device-pairing/sessions/${encodeURIComponent(sessionId)}/consume`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
    },
  );
  if (!res.ok) {
    throw new Error(await res.text());
  }
}

export class DevicePairingAttachError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "DevicePairingAttachError";
    this.status = status;
  }
}

export async function attachPairingByCode(
  shortCode: string,
): Promise<PairingAttachment> {
  const consumerKey = createPairingConsumerKey();
  const res = await fetchWithTransientRetry(`${API_BASE_URL}/api/device-pairing/sessions/attach`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      short_code: shortCode.trim().toUpperCase(),
      consumer_key: consumerKey,
    }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new DevicePairingAttachError(res.status, t || res.statusText);
  }
  const data = (await res.json()) as { session_id: string };
  return { sessionId: data.session_id, consumerKey };
}
